/**
 * Gigs Type System
 *
 * This file defines the type hierarchy for gigs in the application.
 * - GigBase: Base interface with common properties for all gig types
 * - CollaborationGigBase: Base interface for AvailableGig and InvitedGig
 * - AvailableGig: For "Suggested Gigs"
 * - InvitedGig: For "My Invites" - could be shown both in "Your Gigs" that are invited and "Invited Gigs" preview section
 * - PaidGig: For gigs in "Your Gigs" that are pending, active, completed, or archived
 */

import {SocialNetwork} from './platforms';

// Fee mode constants
export const FEE_MODE = {
  FIXED: 'fixed',
  SUGGESTED: 'suggested',
  BID: 'bid',
} as const;

export type FeeMode = (typeof FEE_MODE)[keyof typeof FEE_MODE] | null;

export interface DeliverableCounts {
  videos?: number;
  photos?: number;
  blogs?: number;
  mixedMedia?: number;
  feed?: number;
  reels?: number;
  stories?: number;
  total: number;
}

// Base interface with common properties for all gig types
export interface GigBase {
  // Core identifiers
  id: string;

  // Brand information
  brand: string;
  campaignImage?: string;

  // Campaign details
  campaignName: string;

  // Content requirements
  deliverableCounts?: DeliverableCounts;

  // Additional metadata
  network?: SocialNetwork;
}

// Collaboration Gig type (for Suggested Gigs and Invites)
export interface CollaborationGigBase extends GigBase {
  // Creator profile criteria
  ageRange?: string;
  location?: string;
  gender?: string;
  specialty?: string;
  capability?: string;

  // Engagement metrics
  unlocksAt?: number;
  locksAbove?: number;

  // Financial details
  feeType: FeeMode;
  feeAmount: number | null;
  feeSuggestedMin: number | null;
  feeSuggestedMax: number | null;
  commissionRate: number | null;

  // Post requirements
  postRequirements?: {
    feed: number;
    story: number;
    reels: number;
  };

  // Objective progress percentage
  objectivePercent: number;
}

export interface CollaborationGig extends CollaborationGigBase {
  type: 'collaboration';
}

export interface AvailableGig extends CollaborationGigBase {
  type: 'available';
}

export interface InvitedGig extends CollaborationGigBase {
  type: 'invite';

  // Status information
  recruitedStatus: 'applied' | 'dismissed' | 'pending';
  invitedStatus?: 'applied' | 'dismissed';
  invitedAt?: Date;
  recruitedAt?: Date;
  brandDismissedAt?: Date;
  creatorDismissedAt?: Date;

  brandSuggested?: boolean;
  origination?: string;
}

// Paid Gig type (for Your Gigs section - pending, active, completed, archived)
export interface PaidGig extends GigBase {
  type: 'paid';

  // Status information
  status: 'pending' | 'active' | 'completed' | 'archived';

  // Due date
  dueDate?: Date;

  // Payment information
  payment: {
    amount: number;
    status: 'pending' | 'paid' | 'processing';
  };

  // Progress tracking
  progress?: {
    completed: number;
    total: number;
    percentage: number;
  };

  // Submission information
  submissions?: {
    submitted: number;
    accepted: number;
    rejected: number;
    pending: number;
  };
}
